import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import { api, formatUSD } from "../lib/api";
import { toast } from "sonner";
import {
  UsersThree, CreditCard, CurrencyDollar, HandCoins,
  Money, Clock, CheckCircle, Trash, Printer,
} from "@phosphor-icons/react";
import { buildAgentSelfPrintableReport } from "./agent/agentSelfPrintableReport";
import { ReceiverDetailDialog } from "./agent/ReceiverDetailDialog";
import PostIssueDeliveryDialog from "./agent/PostIssueDeliveryDialog";
import AgentBankInfoWall from "./agent/AgentBankInfoWall";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { useT } from "../lib/i18n";

/**
 * AgentPortal — self-service workspace for users with role=agent.
 * Lets the agent register customers, issue cards / top up / collect cash on
 * their behalf, and view the commission they've earned.
 */
export default function AgentPortal() {
  const t = useT();
  const [me, setMe] = useState(null);
  const [commissions, setCommissions] = useState({ rows: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openReceiver, setOpenReceiver] = useState(null); // email of receiver to drill into
  // After a card is successfully issued (cash or Stripe), open the
  // delivery dialog so the agent can email or text the card directly to
  // the receiver. This card_id stays set until the agent dismisses.
  const [deliveryCardId, setDeliveryCardId] = useState(null);
  // Hard-gate for the bank-info wall. Stays false until /agent/payout-bank
  // reports complete=true (or the agent fills + saves the wall form).
  // While false, all portal action surfaces (issue-card, etc.) render
  // inert so an agent can't earn unpayable commissions.
  const [bankReady, setBankReady] = useState(false);
  // When set, opens the same bank-info wall in dismissible "edit" mode so
  // the agent can update their payout info after the initial save.
  const [editingBank, setEditingBank] = useState(false);

  const refresh = useCallback(async (opts = {}) => {
    try {
      const qParam = (opts.q ?? search).trim();
      const cUrl = qParam
        ? `/agent/commissions?limit=200&q=${encodeURIComponent(qParam)}`
        : "/agent/commissions?limit=50";
      const [meR, comR] = await Promise.all([
        api.get("/agent/me"),
        api.get(cUrl),
      ]);
      setMe(meR.data);
      setCommissions(comR.data);
    } catch (e) {
      toast.error(t("agent.toast.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [t, search]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);

  // Stripe Checkout success / cancel redirect handler.
  // The new agent issuance flow redirects back here with
  // `/agent?stripe_issued=1&card_id=…&session_id=…` (or stripe_issued=0 on
  // cancel). We actively reconcile with Stripe (rather than passively
  // polling the card status) so the agent never gets stuck waiting on a
  // delayed / missing webhook. The reconcile endpoint queries Stripe
  // directly and finalizes the card + records the commission in one shot,
  // mirroring what the webhook would do.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const issued = searchParams.get("stripe_issued");
    const cardId = searchParams.get("card_id");
    const sessionId = searchParams.get("session_id");
    if (!issued) return;
    if (issued === "0") {
      toast.message(t("agent.toast.stripe_cancelled"));
      setSearchParams({}, { replace: true });
      return;
    }
    // issued === "1"
    let cancelled = false;
    let attempts = 0;
    const tryReconcile = async () => {
      attempts += 1;
      try {
        // Active reconcile: hits Stripe API server-side and finalizes the
        // card immediately if paid. Survives webhook failures.
        if (sessionId) {
          const r = await api.get(`/checkout/status/${sessionId}`);
          if (!cancelled && r.data?.payment_status === "paid") {
            toast.success(t("agent.toast.stripe_card_active"));
            await refresh();
            if (cardId) setDeliveryCardId(cardId);
            setSearchParams({}, { replace: true });
            return;
          }
        } else if (cardId) {
          // Fallback when only card_id is available
          const r = await api.get(`/cards/${cardId}`);
          if (!cancelled && r.data?.status === "active") {
            toast.success(t("agent.toast.stripe_card_active"));
            await refresh();
            setDeliveryCardId(cardId);
            setSearchParams({}, { replace: true });
            return;
          }
        }
      } catch (e) { /* swallow — retry */ }
      if (!cancelled && attempts < 6) {
        setTimeout(tryReconcile, 1500);
      } else if (!cancelled) {
        toast.message(t("agent.toast.stripe_card_pending"));
        await refresh();
        setSearchParams({}, { replace: true });
      }
    };
    tryReconcile();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("stripe_issued"), searchParams.get("card_id"), searchParams.get("session_id")]);

  // Debounced search: re-fetch 350ms after the user stops typing.
  // Skip the very first render (initial mount is already handled above)
  // so we don't double-fetch on page load.
  const didMountRef = React.useRef(false);
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    const t1 = setTimeout(() => { refresh({ q: search }); }, 350);
    return () => clearTimeout(t1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const deleteOwnCommission = async (row) => {
    if (row.status === "paid") {
      toast.error(t("agent.commissions.cannot_delete_paid"));
      return;
    }
    if (!window.confirm(t("agent.commissions.delete_confirm", { amount: formatUSD(row.commission_amount) }))) return;
    try {
      await api.delete(`/agent/commissions/${row.id}`);
      toast.success(t("agent.commissions.delete_success"));
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("agent.commissions.delete_failed"));
    }
  };

  const printCommissions = () => {
    // Print whatever's currently on screen (respects the active search
    // filter). The print helper opens a self-contained HTML doc in a new
    // tab and auto-triggers window.print().
    const res = buildAgentSelfPrintableReport(me, commissions.rows, { searchQuery: search });
    if (res?.fallback) {
      toast.message(t("agent.commissions.print_popup_blocked"));
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#4A5C50]">{t("agent.loading")}</div>;

  const summary = commissions.summary || {};
  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]">{t("agent.eyebrow")}</p>
            <h1 className="serif text-5xl mt-2" data-testid="agent-portal-heading">
              {me?.full_name || t("agent.fallback_name")}
            </h1>
            <p className="text-[#4A5C50] mt-2 mono text-xs">
              {t("agent.code_line.code_label")} · <span className="text-[#112318] font-semibold">{me?.agent_code}</span>
              {" · "}{t("agent.code_line.commission_label")} <span className="text-[#112318] font-semibold">{me?.commission_percent}%</span>
              {bankReady && (
                <>
                  {" · "}
                  <button
                    type="button"
                    onClick={() => setEditingBank(true)}
                    className="text-[#184A30] underline underline-offset-2 hover:text-[#0F3320] text-xs font-medium"
                    data-testid="edit-payout-bank-btn"
                  >
                    Edit payout bank
                  </button>
                </>
              )}
            </p>
          </div>
          <div className="bg-[#184A30] text-white rounded-2xl px-6 py-4 min-w-[260px]" data-testid="agent-earnings-tile">
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#8BA888]">{t("agent.earnings.label")}</p>
            <p className="serif text-4xl mt-1">{formatUSD(summary?.unpaid?.total ?? 0)}</p>
            <p className="text-xs text-[#8BA888] mt-2">{t("agent.earnings.sub", { n: summary?.unpaid?.count ?? 0 })}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-4 mt-10">
          <Stat icon={<HandCoins size={20} weight="duotone" />} label={t("agent.stat.week")} value={formatUSD(summary?.week?.total ?? 0)} sub={t("agent.stat.txns", { n: summary?.week?.count ?? 0 })} testId="agent-week" />
          <Stat icon={<CurrencyDollar size={20} weight="duotone" />} label={t("agent.stat.month")} value={formatUSD(summary?.month?.total ?? 0)} sub={t("agent.stat.txns", { n: summary?.month?.count ?? 0 })} testId="agent-month" />
          <Stat icon={<Money size={20} weight="duotone" />} label={t("agent.stat.year")} value={formatUSD(summary?.year?.total ?? 0)} sub={t("agent.stat.txns", { n: summary?.year?.count ?? 0 })} testId="agent-year" />
          <Stat icon={<UsersThree size={20} weight="duotone" />} label={t("agent.stat.customers")} value={me?.customer_count ?? 0} sub={t("agent.stat.active")} testId="agent-customer-count" />
        </div>

        <div className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="agent-issue-card-section">
          <div>
            <h2 className="serif text-3xl">{t("agent.issue.title")}</h2>
            <p className="text-sm text-[#4A5C50] mt-1 max-w-xl">{t("agent.issue.subtitle")}</p>
          </div>
          <IssueCardDialog
            onIssued={refresh}
            onCardIssued={(cid) => setDeliveryCardId(cid)}
            disabled={!bankReady}
          />
        </div>

        <div className="flex items-center justify-between mt-14 gap-4 flex-wrap">
          <h2 className="serif text-3xl">{t("agent.commissions.title")}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative w-full sm:w-80" data-testid="agent-commissions-search-wrap">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("agent.commissions.search_placeholder")}
                className="w-full pl-10 pr-9 py-2.5 rounded-full border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:border-[#184A30] focus:ring-1 focus:ring-[#184A30]"
                data-testid="agent-commissions-search-input"
              />
              <svg aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5C50]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5C50] hover:text-[#112318]"
                  aria-label={t("agent.commissions.search_clear")}
                  data-testid="agent-commissions-search-clear"
                >
                  ×
                </button>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={printCommissions}
              disabled={commissions.rows.length === 0}
              className="shrink-0"
              data-testid="agent-commissions-print-btn"
              title={t("agent.commissions.print_title")}
            >
              <Printer size={14} className="mr-1.5" /> {t("agent.commissions.print_btn")}
            </Button>
          </div>
        </div>
        <div className="bg-white border border-[#E5E0D8] rounded-2xl mt-4 overflow-hidden" data-testid="agent-commissions-table">
          {commissions.rows.length === 0 ? (
            <div className="p-10 text-center text-[#4A5C50]">
              {search ? t("agent.commissions.empty_search", { q: search }) : t("agent.commissions.empty")}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[#4A5C50] text-xs uppercase tracking-widest">
                <tr>
                  <th className="p-4">{t("agent.commissions.col_when")}</th>
                  <th className="p-4">{t("agent.commissions.col_receiver")}</th>
                  <th className="p-4">{t("agent.commissions.col_customer")}</th>
                  <th className="p-4">{t("agent.commissions.col_type")}</th>
                  <th className="p-4 text-right">{t("agent.commissions.col_txn")}</th>
                  <th className="p-4 text-right">{t("agent.commissions.col_commission")}</th>
                  <th className="p-4">{t("agent.commissions.col_status")}</th>
                  <th className="p-4 text-right">{t("agent.commissions.col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {commissions.rows.map((r) => (
                  <tr key={r.id} className="border-t border-[#E5E0D8]" data-testid={`agent-commission-row-${r.id}`}>
                    <td className="p-4 text-xs text-[#4A5C50]">{new Date(r.earned_at).toLocaleString()}</td>
                    <td className="p-4">
                      {(r.receiver_name || r.receiver_email || r.receiver_phone) ? (
                        <button
                          type="button"
                          onClick={() => setOpenReceiver(r.receiver_email || r.customer_email || "")}
                          disabled={!(r.receiver_email || r.customer_email)}
                          className="text-left rounded-md -mx-1 px-1 py-0.5 hover:bg-[#F0E6D6] hover:text-[#0F3320] transition disabled:cursor-default disabled:hover:bg-transparent"
                          title={t("agent.commissions.open_receiver_title")}
                          data-testid={`agent-commission-receiver-${r.id}`}
                        >
                          {r.receiver_name && <div className="font-semibold text-[#112318] hover:underline decoration-dotted underline-offset-4">{r.receiver_name}</div>}
                          {r.receiver_email && <div className="text-xs text-[#4A5C50] mono">{r.receiver_email}</div>}
                          {r.receiver_phone && <div className="text-xs text-[#4A5C50] mono">{r.receiver_phone}</div>}
                        </button>
                      ) : (
                        <span className="text-[#C7C0B4] text-xs">—</span>
                      )}
                    </td>
                    <td className="p-4 mono text-xs">{r.customer_email}</td>
                    <td className="p-4">
                      <TypePill type={r.transaction_type} />
                    </td>
                    <td className="p-4 text-right mono">{formatUSD(r.transaction_amount)}</td>
                    <td className="p-4 text-right font-semibold text-emerald-700">{formatUSD(r.commission_amount)}</td>
                    <td className="p-4">
                      {r.status === "paid" ? (
                        <span className="inline-flex items-center gap-1 text-[11px] tracking-widest uppercase text-emerald-700">
                          <CheckCircle size={14} weight="fill" /> {t("agent.commissions.paid")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] tracking-widest uppercase text-amber-700">
                          <Clock size={14} weight="duotone" /> {t("agent.commissions.unpaid")}
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      {r.status === "unpaid" ? (
                        <button
                          type="button"
                          onClick={() => deleteOwnCommission(r)}
                          className="text-rose-700 hover:text-rose-900 transition"
                          title={t("agent.commissions.delete_title")}
                          data-testid={`agent-commission-delete-${r.id}`}
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      ) : (
                        <span className="text-[#C7C0B4] text-xs" title={t("agent.commissions.cannot_delete_paid_short")}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {commissions.rows.length > 0 && (
                <tfoot className="bg-[#FBF9F6] border-t-2 border-[#184A30]" data-testid="agent-commissions-total-row">
                  <tr>
                    <td colSpan={4} className="p-4 text-xs uppercase tracking-widest text-[#4A5C50] font-semibold">
                      {t("agent.commissions.total_label", { n: commissions.rows.length })}
                    </td>
                    <td className="p-4 text-right mono text-xs text-[#4A5C50]">
                      {formatUSD(commissions.rows.reduce((s, r) => s + (Number(r.transaction_amount) || 0), 0))}
                    </td>
                    <td className="p-4 text-right mono text-base font-bold text-[#184A30]" data-testid="agent-commissions-grand-total">
                      {formatUSD(commissions.rows.reduce((s, r) => s + (Number(r.commission_amount) || 0), 0))}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </div>
      <ReceiverDetailDialog
        open={!!openReceiver}
        onOpenChange={(v) => { if (!v) setOpenReceiver(null); }}
        receiverEmail={openReceiver}
        agentMe={me}
      />
      <PostIssueDeliveryDialog
        cardId={deliveryCardId}
        onClose={() => setDeliveryCardId(null)}
      />
      <AgentBankInfoWall onComplete={() => setBankReady(true)} />
      {editingBank && (
        <AgentBankInfoWall
          mode="edit"
          onComplete={() => { /* no-op — bank stays ready */ }}
          onDismiss={() => setEditingBank(false)}
        />
      )}
    </div>
  );
}

function Stat({ icon, label, value, sub, testId }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 card-lift" data-testid={testId}>
      <div className="text-[#184A30]">{icon}</div>
      <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] mt-4">{label}</p>
      <p className="serif text-3xl mt-1">{value}</p>
      {sub && <p className="text-xs text-[#4A5C50] mt-1">{sub}</p>}
    </div>
  );
}

function TypePill({ type }) {
  const t = useT();
  const map = {
    issuance: { label: t("agent.type.issuance"), color: "bg-[#184A30]/10 text-[#184A30]" },
    topup: { label: t("agent.type.topup"), color: "bg-amber-100 text-amber-800" },
    cash_deposit: { label: t("agent.type.cash_deposit"), color: "bg-emerald-100 text-emerald-800" },
  };
  const m = map[type] || { label: type, color: "bg-stone-100 text-stone-700" };
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] tracking-widest uppercase font-semibold ${m.color}`}>
      {m.label}
    </span>
  );
}

function IssueCardDialog({ customer, onIssued, onCardIssued, disabled = false }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ receiver_name: "", receiver_email: "", receiver_phone: "", source_amount: 50, note: "" });
  const [paymentMode, setPaymentMode] = useState("cash"); // "cash" | "card"
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // When called from a customer row, link to that customer; otherwise
      // the backend auto-creates a placeholder customer from receiver_email.
      const payload = {
        ...(customer?.id ? { customer_id: customer.id } : {}),
        ...form,
        source_amount: Number(form.source_amount),
      };
      if (paymentMode === "card") {
        // Stripe-funded flow: backend creates a pending_payment card +
        // Stripe Checkout session, then we redirect the agent's browser to
        // Stripe's hosted card form so the customer's card can be entered.
        const r = await api.post("/agent/cards/issue/stripe-checkout", {
          ...payload,
          origin_url: window.location.origin,
        });
        toast.success(t("agent.toast.redirecting_to_stripe"));
        // Small delay so the toast renders before the redirect away.
        setTimeout(() => { window.location.href = r.data.checkout_url; }, 350);
        return;
      }
      // Cash-collected flow (current behaviour): backend immediately
      // activates the card and records the commission row.
      const r = await api.post("/agent/cards/issue", payload);
      toast.success(t("agent.toast.card_issued", { amount: formatUSD(r.data.commission?.commission_amount ?? 0) }));
      setOpen(false);
      onIssued?.();
      // Hand the new card_id to the parent so it can pop the delivery
      // dialog. We do this AFTER closing this dialog so the two modals
      // don't visually overlap.
      const newCardId = r.data?.card?.id;
      if (newCardId) onCardIssued?.(newCardId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("agent.toast.card_issue_failed"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!disabled) setOpen(v); }}>
      <DialogTrigger asChild>
        {customer ? (
          <Button size="sm" variant="outline" disabled={disabled} data-testid={`agent-issue-card-${customer.id}`}>
            <CreditCard size={14} className="mr-1" /> {t("agent.actions.issue_card")}
          </Button>
        ) : (
          <Button
            disabled={disabled}
            className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white px-6 py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="agent-issue-card-toplevel"
            title={disabled ? "Add commission payout bank info to enable issuing." : undefined}
          >
            <CreditCard size={18} weight="duotone" className="mr-2" /> {t("agent.issue.cta")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8]">
        <DialogHeader>
          <DialogTitle className="serif text-3xl">
            {customer
              ? t("agent.dialog.issue_card.title", { name: customer.full_name })
              : t("agent.dialog.issue_card.title_no_customer")}
          </DialogTitle>
          <DialogDescription className="text-[#4A5C50]">{t("agent.dialog.issue_card.desc")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("agent.dialog.field.receiver_name")}</Label>
              <input value={form.receiver_name} onChange={e => setForm({ ...form, receiver_name: e.target.value })} required className="underline-input" data-testid="agent-issue-receiver-name" />
            </div>
            <div>
              <Label>{t("agent.dialog.field.receiver_email")}</Label>
              <input type="email" value={form.receiver_email} onChange={e => setForm({ ...form, receiver_email: e.target.value })} required className="underline-input mono" data-testid="agent-issue-receiver-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("agent.dialog.field.receiver_phone")}</Label>
              <input value={form.receiver_phone} onChange={e => setForm({ ...form, receiver_phone: e.target.value })} className="underline-input mono" data-testid="agent-issue-receiver-phone" />
            </div>
            <div>
              <Label>{t("agent.dialog.field.amount_usd")}</Label>
              <input type="number" min={1} max={2000} step={1} value={form.source_amount} onChange={e => setForm({ ...form, source_amount: e.target.value })} required className="underline-input mono text-2xl" data-testid="agent-issue-amount" />
            </div>
          </div>
          <div>
            <Label>{t("agent.dialog.field.note")}</Label>
            <input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className="underline-input" data-testid="agent-issue-note" />
          </div>
          <div className="rounded-xl border border-[#E5E0D8] bg-white p-3 mt-1" data-testid="agent-issue-payment-mode">
            <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">
              {t("agent.dialog.field.payment_mode")}
            </Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                type="button"
                onClick={() => setPaymentMode("cash")}
                className={`text-left rounded-lg border p-3 transition ${paymentMode === "cash" ? "border-[#184A30] bg-[#F0F4F0]" : "border-[#E5E0D8] bg-white hover:border-[#4A5C50]"}`}
                data-testid="agent-issue-mode-cash"
              >
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-4 h-4 rounded-full border-2 ${paymentMode === "cash" ? "border-[#184A30] bg-[#184A30]" : "border-[#4A5C50]"}`} />
                  <span className="font-semibold text-sm">{t("agent.dialog.mode.cash")}</span>
                </div>
                <p className="text-xs text-[#4A5C50] mt-1.5">{t("agent.dialog.mode.cash_help")}</p>
              </button>
              <button
                type="button"
                onClick={() => setPaymentMode("card")}
                className={`text-left rounded-lg border p-3 transition ${paymentMode === "card" ? "border-[#184A30] bg-[#F0F4F0]" : "border-[#E5E0D8] bg-white hover:border-[#4A5C50]"}`}
                data-testid="agent-issue-mode-card"
              >
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-4 h-4 rounded-full border-2 ${paymentMode === "card" ? "border-[#184A30] bg-[#184A30]" : "border-[#4A5C50]"}`} />
                  <span className="font-semibold text-sm">{t("agent.dialog.mode.card")}</span>
                </div>
                <p className="text-xs text-[#4A5C50] mt-1.5">{t("agent.dialog.mode.card_help")}</p>
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>{t("agent.dialog.cancel")}</Button>
            <Button type="submit" disabled={busy} className="bg-[#184A30] text-white" data-testid="agent-issue-submit">
              {busy
                ? (paymentMode === "card" ? t("agent.dialog.redirecting") : t("agent.dialog.issuing"))
                : (paymentMode === "card" ? t("agent.dialog.continue_to_stripe") : t("agent.dialog.issue_card_btn"))}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
